import type { HomeyAPI, Variable } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type VariableProps = {
  [K in keyof Variable as Variable[K] extends Function ? never : K]: Variable[K];
};

export type VariablesData = { [key: string]: Variable };
export type VariablesById = { [key: string]: VariableProps };

interface State {
  data: VariablesData | null;
  byId: VariablesById | null;
  isLoading: boolean;
  error: unknown;
}

export type VariablesStoreState = State;

export class VariablesStore extends BaseApiStore<State, Variable> {
  constructor() {
    super('variables');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.logic, reference: this });

      if (abortController.signal.aborted) return;

      const variablesPromise = api.logic.getVariables({
        // @ts-ignore
        $cache: cache,
      });
      const [variablesData] = await Promise.all([variablesPromise]);

      if (abortController.signal.aborted) return;

      const variables = Object.values(variablesData);

      const data: VariablesData = {};
      const byId: VariablesById = {};

      for (const variableInstance of variables) {
        const variableReference = { ...variableInstance };

        data[variableReference.id] = variableInstance;
        byId[variableReference.id] = variableReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.logic, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.logic.on('variable.create', this.handleCreate);
    api.logic.on('variable.update', this.handleUpdate);
    api.logic.on('variable.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.logic.off('variable.create', this.handleCreate);
    api.logic.off('variable.update', this.handleUpdate);
    api.logic.off('variable.delete', this.handleDelete);
  }

  getInstanceFromCache(variable: { id: string }) {
    return this.getCache()[variable.id];
  }

  getCache(): { [key: string]: Variable } {
    const api = this.getApi();
    const cache = api.logic.__cache['variable'];
    return cache;
  }

  handleCreate = (createdVariable: Variable) => {
    this.DEBUG && console.info(`create:variable:${createdVariable.id}`);
    const state = this.get();
    const variableInstance = this.getInstanceFromCache(createdVariable);

    if (variableInstance == null) return;

    const variableReference = { ...variableInstance };

    this.set({
      data: {
        ...state.data,
        [createdVariable.id]: variableInstance,
      },
      byId: {
        ...state.byId,
        [createdVariable.id]: variableReference,
      },
    });
  };

  handleUpdate = (updatedVariable: Variable) => {
    this.DEBUG && console.info(`update:variable:${updatedVariable.id}`);

    this.addToQueue(updatedVariable.id, updatedVariable);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedVariable] of Object.entries(updateQueue)) {
        const variableInstance = this.getInstanceFromCache(queuedVariable);

        if (state.data == null) {
          this.DEBUG && console.info(`update:variable:missing-data`);
          continue;
        }

        if (variableInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:variable:unknown:${id}`);
          continue;
        }

        const variableReference = { ...variableInstance };

        nextById[id] = variableReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:variable:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedVariable: Variable) => {
    this.DEBUG && console.info(`delete:variable:${deletedVariable.id}`);
    const state = this.get();

    if (state.data == null) {
      this.DEBUG && console.log(`delete:variable:missing-data`);
      return;
    }

    const variableInstance = state.data[deletedVariable.id];

    if (variableInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[variableInstance.id];
    delete nextById[variableInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const variables = Object.values(cache ?? {});
    const data: VariablesData = {};
    const byId: VariablesById = {};

    for (const variableInstance of variables) {
      const variableReference = { ...variableInstance };
      data[variableReference.id] = variableInstance;
      byId[variableReference.id] = variableReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }

  getVariables() {
    return this.get().byId;
  }
}

export const variablesStore = new VariablesStore();
